/**
 * Registers GPU instancing templates from the same React kits as pre-simulation detail view.
 * One kit at a time, deferred after 3D mount — avoids freezing on 2D→3D switch.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CleanroomWorkcell3D } from '../../components/canvas/amhs/CleanroomWorkcell/CleanroomWorkcell3D';
import { Awp300 } from '../../components/canvas/amhs/Equipment/Awp300/Awp300';
import { IndustrialVerticalLifter3D } from '../../components/canvas/amhs/VerticalLifter/IndustrialVerticalLifter3D';
import { CircleJunctionTurntableMesh3D } from '../../components/canvas/DualRailTrack3D';
import { OhtVehicle3DModel } from '../../components/canvas/OhtVehicle3DModel';
import { FOUP } from '../../components/canvas/amhs/FOUP/FOUP';
import { RailElevationContext } from '../../components/canvas/railElevationContext';
import { AMHS_KIT_TEMPLATE_DIMS } from './amhsKitTemplateDims';
import {
  flushAmhsKitTemplateNotify,
  registerAmhsKitTemplateFromScene,
  setAmhsKitTemplateNotifySuspended,
  type AmhsKitTemplateKey,
  isAmhsKitTemplateReady,
} from './amhsKitTemplateStore';
import { exportCapturedAmhsKitGlbs } from './amhsKitGlbExport';

const CAPTURE_OFFSET: [number, number, number] = [750_000, 0, 750_000];
const CAPTURE_LAYER = 1;

/** Set VITE_FAB_LIVE_KIT_CAPTURE=false in .env to skip (procedural instancing only). */
const LIVE_KIT_CAPTURE_ENABLED = import.meta.env.VITE_FAB_LIVE_KIT_CAPTURE !== 'false';

/**
 * Stockers use procedural InstancedStocker templates — live capture expands rack
 * InstancedMesh into thousands of clones and blocks the UI for seconds.
 */
const LIVE_CAPTURE_ORDER: AmhsKitTemplateKey[] = [
  'FoupDefault',
  'OhtVehicle',
  'JunctionRing',
  'Awp300',
  'IndustrialVerticalLifter3D',
  'CleanroomWorkcell',
];

function countRenderableMeshes(root: THREE.Object3D): number {
  let n = 0;
  root.traverse((child) => {
    if (!child.visible) return;
    if (child instanceof THREE.Mesh || child instanceof THREE.InstancedMesh) n += 1;
  });
  return n;
}

function SingleKitCapture({
  templateKey,
  children,
  onCaptured,
}: {
  templateKey: AmhsKitTemplateKey;
  children: React.ReactNode;
  onCaptured: () => void;
}) {
  const rootRef = useRef<THREE.Group>(null);
  const frames = useRef(0);
  const done = useRef(false);

  useFrame(() => {
    if (done.current) return;
    const root = rootRef.current;
    if (!root) return;
    frames.current += 1;
    if (frames.current < 2) return;
    if (countRenderableMeshes(root) === 0) return;
    if (isAmhsKitTemplateReady(templateKey)) {
      done.current = true;
      onCaptured();
      return;
    }
    done.current = true;
    registerAmhsKitTemplateFromScene(templateKey, root, { silent: true });
    onCaptured();
  });

  return (
    <group ref={rootRef} name={`AmhsKitCapture-${templateKey}`} layers={CAPTURE_LAYER}>
      {children}
    </group>
  );
}

function kitCaptureNode(key: AmhsKitTemplateKey): React.ReactNode {
  const wc = AMHS_KIT_TEMPLATE_DIMS.CleanroomWorkcell;
  const awp = AMHS_KIT_TEMPLATE_DIMS.Awp300;
  const lift = AMHS_KIT_TEMPLATE_DIMS.IndustrialVerticalLifter3D;
  const junc = AMHS_KIT_TEMPLATE_DIMS.JunctionRing;
  const oht = AMHS_KIT_TEMPLATE_DIMS.OhtVehicle;

  switch (key) {
    case 'CleanroomWorkcell':
      return (
        <CleanroomWorkcell3D
          id="KIT-TEMPLATE-WC"
          width={wc.w}
          height={wc.h}
          depth={wc.d}
          animateRobot={false}
          animateLifter={false}
          opacity={1}
        />
      );
    case 'Awp300':
      return (
        <Awp300
          id="KIT-TEMPLATE-AWP"
          width={awp.w}
          depth={awp.d}
          height={awp.h}
          autoDetail={false}
          detail="high"
          opacity={1}
        />
      );
    case 'IndustrialVerticalLifter3D':
      return (
        <IndustrialVerticalLifter3D
          id="KIT-TEMPLATE-VTU"
          height={lift.h}
          towerWidth={Math.max(3.8, lift.w * 0.45)}
          towerDepth={Math.max(3.8, lift.d * 0.45)}
          conveyorLength={Math.max(6.0, Math.max(lift.w, lift.d) * 0.65)}
          animated={false}
          opacity={1}
        />
      );
    case 'JunctionRing':
      return (
        <CircleJunctionTurntableMesh3D
          ringR={junc.ringR}
          depth={junc.depth}
          trackWidth={junc.trackW}
          junctionW={junc.footprint}
          junctionH={junc.footprint}
          hubAngleDeg={0}
          opacity={1}
        />
      );
    case 'OhtVehicle':
      return (
        <OhtVehicle3DModel
          w={oht.w}
          h={oht.h}
          depth={oht.d}
          opacity={1}
          isLoaded={false}
          showStatusBadge={false}
        />
      );
    case 'FoupDefault':
      return <FOUP scale={1} opacity={1} />;
    default:
      return null;
  }
}

export const AmhsKitTemplateCapture: React.FC = React.memo(() => {
  const [step, setStep] = useState(-1);
  const exportStarted = useRef(false);

  useEffect(() => {
    if (!LIVE_KIT_CAPTURE_ENABLED) return;
    setAmhsKitTemplateNotifySuspended(true);
    const start = () => setStep(0);
    const idleId =
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback(start, { timeout: 1500 })
        : null;
    const timeoutId = idleId == null ? window.setTimeout(start, 400) : null;
    return () => {
      setAmhsKitTemplateNotifySuspended(false);
      if (idleId != null) cancelIdleCallback(idleId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (step === LIVE_CAPTURE_ORDER.length) {
      setAmhsKitTemplateNotifySuspended(false);
      flushAmhsKitTemplateNotify();
    }
  }, [step]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('exportFabGlb')) return;

    const timer = window.setInterval(() => {
      if (!LIVE_CAPTURE_ORDER.every(isAmhsKitTemplateReady)) return;
      if (exportStarted.current) return;
      exportStarted.current = true;
      window.clearInterval(timer);
      void exportCapturedAmhsKitGlbs(LIVE_CAPTURE_ORDER);
    }, 800);

    return () => window.clearInterval(timer);
  }, []);

  if (!LIVE_KIT_CAPTURE_ENABLED) return null;
  if (step < 0 || step >= LIVE_CAPTURE_ORDER.length) return null;

  const key = LIVE_CAPTURE_ORDER[step]!;
  const lift = AMHS_KIT_TEMPLATE_DIMS.IndustrialVerticalLifter3D;

  return (
    <group name="AmhsKitTemplateCapture" position={CAPTURE_OFFSET}>
      <RailElevationContext.Provider value={lift.h}>
        <SingleKitCapture templateKey={key} onCaptured={() => setStep((s) => s + 1)}>
          {kitCaptureNode(key)}
        </SingleKitCapture>
      </RailElevationContext.Provider>
    </group>
  );
});

AmhsKitTemplateCapture.displayName = 'AmhsKitTemplateCapture';
export default AmhsKitTemplateCapture;
